import React, { useMemo, useState } from "react";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import FolderRoundedIcon                 from "@mui/icons-material/FolderRounded";
import ExpandLessRoundedIcon             from "@mui/icons-material/ExpandLessRounded";
import SubdirectoryArrowRightRoundedIcon from "@mui/icons-material/SubdirectoryArrowRightRounded";
import { ds } from "../../styles/designSystem";
import { buildFormTree, folderColor } from "./formTree";

/**
 * 양식 폴더 트리 — 상신 모달과 관리자 화면이 같이 쓴다.
 *
 * 메일 폴더 사이드바와 같은 모양이다: 폴더 아이콘 + 접기 화살표, 하위는 «↳» 로 이어 붙인다.
 * 양식마다 아이콘을 주지 않는 이유는 19개가 제각각 색을 갖는 순간 이름이 아니라 색이 먼저 읽히기
 * 때문이다 — 여기서 고르는 기준은 «무슨 양식인가» 하나뿐이다.
 *
 * @param groups   [{groupCode, groupName, forms:[…]}]
 * @param keyOf    양식 → 고유 키 (선택 비교용)
 * @param selected 지금 선택된 키
 * @param onSelect 양식 클릭
 * @param query    검색어 — 있으면 접힘을 무시한다
 * @param renderRight 양식 줄 오른쪽에 붙일 것(배지 등)
 */
export default function FormTreeNav({
    groups, keyOf, selected, onSelect, query = "", renderRight,
}) {
    const tree = useMemo(() => buildFormTree(groups), [groups]);
    const [collapsed, setCollapsed] = useState(() => new Set());

    const searching = Boolean(query.trim());
    const isOpen = (id) => searching || !collapsed.has(id);
    const toggle = (id) => setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    return (
        <>
            {tree.map((g) => {
                const gid = `g:${g.groupCode}`;
                const total = g.nodes.reduce(
                    (n, x) => n + (x.kind === "folder" ? x.forms.length : 1), 0);
                return (
                    <Box key={gid} sx={{ mb: 0.25 }}>
                        <FolderRow
                            name={g.groupName} count={total} depth={0}
                            open={isOpen(gid)} onToggle={() => toggle(gid)}
                        />
                        <Collapse in={isOpen(gid)}>
                            {g.nodes.map((node) => {
                                if (node.kind === "form") {
                                    return (
                                        <FormRow
                                            key={keyOf(node.form)} depth={1}
                                            label={node.label}
                                            form={node.form}
                                            active={keyOf(node.form) === selected}
                                            onClick={() => onSelect(node.form)}
                                            renderRight={renderRight}
                                        />
                                    );
                                }
                                const fid = `${gid}/f:${node.name}`;
                                return (
                                    <Box key={fid}>
                                        <FolderRow
                                            name={node.name} count={node.forms.length} depth={1}
                                            open={isOpen(fid)} onToggle={() => toggle(fid)}
                                        />
                                        <Collapse in={isOpen(fid)}>
                                            {node.forms.map(({ form, label }) => (
                                                <FormRow
                                                    key={keyOf(form)} depth={2}
                                                    label={label}
                                                    form={form}
                                                    active={keyOf(form) === selected}
                                                    onClick={() => onSelect(form)}
                                                    renderRight={renderRight}
                                                />
                                            ))}
                                        </Collapse>
                                    </Box>
                                );
                            })}
                        </Collapse>
                    </Box>
                );
            })}
        </>
    );
}

/**
 * 폴더 한 줄 — 그룹(depth 0)과 하위 폴더(depth 1)가 같은 컴포넌트를 쓴다.
 *
 * 굵게 쓰는 건 폴더뿐이다. 양식 줄까지 굵으면 어디가 묶음이고 어디가 항목인지 구분이 사라진다.
 */
function FolderRow({ name, count, depth, open, onToggle }) {
    const child = depth > 0;
    return (
        <Stack
            direction="row" alignItems="center" spacing={child ? 0.6 : 1.1}
            onClick={onToggle}
            sx={{
                pl: 1.1 + depth * 1.4, pr: 1.1, py: child ? 0.65 : 0.9,
                mx: 0.3, borderRadius: 1.2, cursor: "pointer",
                "&:hover": { bgcolor: "#f1f5f9" },
            }}>
            {child && (
                <SubdirectoryArrowRightRoundedIcon
                    sx={{ fontSize: 14, color: "#cbd5e1", ml: -0.2, flexShrink: 0 }} />
            )}
            <FolderRoundedIcon
                sx={{ fontSize: child ? 16 : 18, color: folderColor(name, depth), flexShrink: 0 }} />
            <Typography noWrap sx={{
                flex: 1, minWidth: 0,
                fontSize: child ? 13 : 13.5,
                fontWeight: 800,
                color: "#0f172a",
            }}>
                {name}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", fontWeight: 600 }}>{count}</Typography>
            <IconButton
                size="small" onClick={(e) => { e.stopPropagation(); onToggle(); }}
                sx={{ p: 0, color: "#94a3b8", "&:hover": { color: ds.colors.primary, bgcolor: "transparent" } }}>
                <ExpandLessRoundedIcon sx={{
                    fontSize: 16, transition: "transform 160ms ease",
                    transform: open ? "none" : "rotate(180deg)",
                }} />
            </IconButton>
        </Stack>
    );
}

/** 양식 한 줄 — 아이콘 없이 «↳» 와 이름만. 폴더와 달리 보통 굵기로 둔다. */
function FormRow({ label, form, depth, active, onClick, renderRight }) {
    return (
        <Stack
            direction="row" alignItems="flex-start" spacing={0.5}
            onClick={onClick}
            sx={{
                pl: 1.1 + depth * 1.4, pr: 1.1, py: 0.55, mx: 0.3, borderRadius: 1.2,
                cursor: "pointer",
                bgcolor: active ? ds.colors.primaryBg : "transparent",
                "&:hover": { bgcolor: active ? ds.colors.primaryBg : "#f1f5f9" },
            }}>
            <SubdirectoryArrowRightRoundedIcon
                sx={{ fontSize: 14, ml: -0.2, mt: 0.25, flexShrink: 0,
                      color: active ? ds.colors.primarySoft : "#cbd5e1" }} />
            {/* 줄바꿈을 허용한다 — «지출 결의서 (법인카드 결제 건 – 금액 확정 건)» 처럼 긴 이름이
                잘리면 괄호 안(고르는 기준)이 통째로 사라진다. */}
            <Typography sx={{
                flex: 1, minWidth: 0, fontSize: 13, lineHeight: 1.4,
                fontWeight: active ? 700 : 400,
                color: form.active === false ? "#94a3b8"
                     : active ? ds.colors.primaryDark : "#334155",
            }}>
                {label}
            </Typography>
            {renderRight?.(form)}
        </Stack>
    );
}
